import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkline } from './charts.jsx';
import { api } from '../lib/api.js';

export const Card = ({ title, note, actions, children, className = '', pad = true }) => (
  <section className={`card ${className}`} style={pad ? undefined : { padding: 0 }}>
    {(title || actions) && (
      <header className="card-head">
        <div>
          <h2 className="card-title">{title}</h2>
          {note && <div className="card-note">{note}</div>}
        </div>
        {actions && <div className="inline">{actions}</div>}
      </header>
    )}
    {children}
  </section>
);

const THEME_KEY = 'invit.theme';

/** Applies the stored preference before the first paint of either shell. */
export const initTheme = () => {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored) document.documentElement.dataset.theme = stored;
};

export function ThemeToggle() {
  const [theme, setTheme] = React.useState(() => localStorage.getItem(THEME_KEY) || 'system');
  const apply = (next) => {
    setTheme(next);
    if (next === 'system') {
      delete document.documentElement.dataset.theme;
      localStorage.removeItem(THEME_KEY);
    } else {
      document.documentElement.dataset.theme = next;
      localStorage.setItem(THEME_KEY, next);
    }
  };
  return (
    <div className="segmented" role="group" aria-label="Theme">
      {['light', 'system', 'dark'].map((t) => (
        <button key={t} aria-pressed={theme === t} onClick={() => apply(t)}>
          {t === 'light' ? 'Light' : t === 'dark' ? 'Dark' : 'Auto'}
        </button>
      ))}
    </div>
  );
}

export const Field = ({ label, hint, children }) => (
  <div className="field">
    <label>{label}{hint && <span className="muted" style={{ fontWeight: 400 }}> · {hint}</span>}</label>
    {children}
  </div>
);

export const Alert = ({ kind = 'error', children, onRetry }) => (children ? (
  <div className={`alert ${kind}`} role={kind === 'error' ? 'alert' : undefined}>
    <span aria-hidden="true">{kind === 'error' ? '✕' : kind === 'warn' ? '!' : kind === 'ok' ? '✓' : 'i'}</span>
    <div style={{ flex: 1 }}>{children}</div>
    {onRetry && <button className="btn small secondary" onClick={onRetry}>Try again</button>}
  </div>
) : null);

const TONE = {
  draft: 'info', issued: 'ok', delivered: 'ok', confirmed: 'ok', invoiced: 'ok', synced: 'ok',
  recorded: 'ok', approved: 'ok', done: 'ok', active: 'ok',
  partially_delivered: 'warn', pending: 'warn', queued: 'warn', leased: 'warn',
  cancelled: 'bad', failed: 'bad', expired: 'bad', suspended: 'bad', skipped: '',
};
export const Pill = ({ value, label }) => (
  <span className={`pill ${TONE[value] || ''}`}>{label || String(value || '').replace(/_/g, ' ')}</span>
);

export const Empty = ({ title, children, action }) => (
  <div className="empty">
    <div className="big">{title}</div>
    <div>{children}</div>
    {action && <div style={{ marginTop: 14 }}>{action}</div>}
  </div>
);

export const Skeleton = ({ height = 16, width = '100%', style }) => (
  <div className="skeleton" style={{ height, width, ...style }} />
);

export const CardSkeleton = ({ lines = 3 }) => (
  <div className="card">
    <Skeleton height={14} width="38%" />
    <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
      {Array.from({ length: lines }).map((_, i) => <Skeleton key={i} height={12} width={`${92 - i * 11}%`} />)}
    </div>
  </div>
);

const ARROW = { up: '▲', down: '▼', flat: '→' };

/**
 * The dashboard's unit of navigation: a figure, its comparison, and a
 * destination. The whole card is the hit target — never a small "view" link.
 */
export function StatCard({ label, value, unit, delta, foot, to, hero, tone, spark, onClick }) {
  const body = (
    <>
      <div className="label">{label}</div>
      <div className="value num">
        {value}{unit && <small> {unit}</small>}
      </div>
      <div className="foot">
        {delta && (
          <span className={`delta ${delta.dir === 'flat' ? 'flat' : delta.good ? 'up' : 'down'}`}>
            <span aria-hidden="true">{ARROW[delta.dir]}</span>{delta.text}
          </span>
        )}
        {foot && <span className="muted">{foot}</span>}
        {spark && spark.length > 2 && <span style={{ marginLeft: 'auto' }}><Sparkline values={spark} /></span>}
      </div>
      {(to || onClick) && <span className="go" aria-hidden="true">→</span>}
    </>
  );
  const className = `stat ${hero ? 'hero' : ''} ${tone === 'attention' ? 'attention' : ''}`;
  if (to) return <Link className={className} to={to}>{body}</Link>;
  if (onClick) return <button type="button" className={className} onClick={onClick}>{body}</button>;
  return <div className={className}>{body}</div>;
}

/**
 * One screen failing must not blank the application. The boundary keeps the
 * navigation usable and offers a way back.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept in the console for whoever is looking; nothing is sent anywhere.
    console.error('screen failed', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Card title="This screen ran into a problem"
        actions={<button className="btn small" onClick={() => this.setState({ error: null })}>Try again</button>}>
        <div className="card-note">{String(this.state.error.message || this.state.error)}</div>
        <div className="card-note" style={{ marginTop: 8 }}>
          Everything else still works — pick another screen from the left.
        </div>
      </Card>
    );
  }
}

/** Data loader with an explicit retry, so an error state is never a dead end. */
export function useAsync(fn, deps = []) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const reload = React.useCallback(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    fn().then(
      (data) => live && setState({ loading: false, data, error: null }),
      (error) => live && setState({ loading: false, data: null, error: error.message }),
    );
    return () => { live = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(reload, [reload]);
  return { ...state, reload };
}

/**
 * A cursor-paged list. `data` looks like the first page with every loaded row
 * under `key`; a page that arrives after the filter changed is thrown away
 * rather than appended to the wrong list.
 */
export function usePagedList(url, key) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null, cursor: null });
  const [more, setMore] = React.useState({ loading: false, error: null });
  const generation = React.useRef(0);

  const reload = React.useCallback(() => {
    const mine = ++generation.current;
    setState((s) => ({ ...s, loading: true }));
    setMore({ loading: false, error: null });
    api.get(url).then(
      (data) => mine === generation.current
        && setState({ loading: false, data: { ...data, [key]: data[key] || [] }, error: null, cursor: data.next_cursor || null }),
      (error) => mine === generation.current
        && setState({ loading: false, data: null, error: error.message, cursor: null }),
    );
  }, [url, key]);
  React.useEffect(() => { reload(); }, [reload]);

  const loadMore = async () => {
    if (state.loading || !state.cursor) return;
    const mine = generation.current;
    setMore({ loading: true, error: null });
    try {
      const res = await api.get(`${url}${url.includes('?') ? '&' : '?'}cursor=${encodeURIComponent(state.cursor)}`);
      if (mine !== generation.current) return;
      setState((s) => ({
        ...s,
        data: { ...s.data, [key]: [...s.data[key], ...(res[key] || [])] },
        cursor: res.next_cursor || null,
      }));
      setMore({ loading: false, error: null });
    } catch (e) {
      if (mine === generation.current) setMore({ loading: false, error: e.message });
    }
  };

  return {
    loading: state.loading, data: state.data, error: state.error, reload, loadMore,
    hasMore: Boolean(state.cursor) && !state.loading, loadingMore: more.loading, moreError: more.error,
  };
}

/** The foot of a paged list: the button while more rows remain, and why the last attempt failed. */
export const LoadMore = ({ list }) => (list.hasMore || list.moreError ? (
  <div style={{ padding: 12, textAlign: 'center' }}>
    <Alert onRetry={list.loadMore}>{list.moreError}</Alert>
    {list.hasMore && !list.moreError && (
      <button className="btn secondary" disabled={list.loadingMore} onClick={list.loadMore}>
        {list.loadingMore ? 'Loading…' : 'Load more'}
      </button>
    )}
  </div>
) : null);
